import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Mail, Search, SlidersHorizontal, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ReportTableScroll from "@/components/ReportTableScroll";
import EnrolledHouseholdRow from "@/components/EnrolledHouseholdRow";
import {
  DEFAULT_HOUSEHOLD_FILTERS, filterEnrolledHouseholds, activeHouseholdFilterCount,
} from "@/lib/sharing-tree-household-filters";

const PAGE_SIZES = [25, 50, 100];
const EMPTY_SET = new Set();
const selectCls = "w-full text-sm rounded-lg border px-2.5 py-2 bg-white min-h-[40px]";

function searchPlaceholder(hidden) {
  const parts = ["household or member ID"];
  if (!hidden.includes("names")) parts.push("name");
  if (!hidden.includes("phone")) parts.push("phone");
  if (!hidden.includes("email")) parts.push("email");
  if (!hidden.includes("address")) parts.push("address");
  return `Search by ${parts.length > 1 ? `${parts.slice(0, -1).join(", ")} or ${parts[parts.length - 1]}` : parts[0]}`;
}

function FilterSelect({ id, label, value, onChange, children }) {
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="block text-[11px] font-semibold uppercase tracking-wider mb-1" style={{ color: "var(--fb-text-muted)" }}>{label}</label>
      <select id={id} data-testid={id} value={value} onChange={(e) => onChange(e.target.value)}
        className={selectCls} style={{ borderColor: "var(--fb-border)" }}>
        {children}
      </select>
    </div>
  );
}

export default function EnrolledHouseholdsCard({
  households, year, onRefresh, isPriorYear, canEdit = false, canEditProfile = canEdit, onEdit,
  pickupSlots = [], showFamilyItem = false, selectedIds = EMPTY_SET, onToggleSelected, onSelectAll,
  onSendSelected, hiddenFields = [], exportControl,
}) {
  const [deleting, setDeleting] = useState({});
  const [confirmPerm, setConfirmPerm] = useState(null);
  const [resettingReminder, setResettingReminder] = useState({});
  const [resendingReminder, setResendingReminder] = useState({});
  const [editingWindow, setEditingWindow] = useState(null);
  const [savingWindow, setSavingWindow] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [filters, setFilters] = useState(DEFAULT_HOUSEHOLD_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const writable = canEdit && !isPriorYear;
  const namesHidden = hiddenFields.includes("names");
  const emailHidden = hiddenFields.includes("email");

  function toggleMembers(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function run(setter, h, value, fn) {
    setter((p) => ({ ...p, [h.id]: value }));
    try { await fn(); onRefresh(); } catch (e) { alert(formatApiError(e)); }
    finally { setter((p) => ({ ...p, [h.id]: value === true ? false : null })); }
  }

  const actions = {
    setEditingWindow, setConfirmPerm,
    resetReminder: (h) => run(setResettingReminder, h, true, () => api.post(`/sharing-tree/households/${h.id}/reset-reminder`)),
    resendReminder: (h) => run(setResendingReminder, h, true, () => api.post(`/sharing-tree/households/${h.id}/send-reminder`)),
    savePickupWindow: (h, w) => run(setSavingWindow, h, true, async () => {
      await api.patch(`/sharing-tree/households/${h.id}/pickup-window`, { pickup_window: w || null });
      setEditingWindow(null);
    }),
    softDelete: (h) => run(setDeleting, h, "deleting", () => api.delete(`/sharing-tree/households/${h.id}`)),
    restore: (h) => run(setDeleting, h, "restoring", () => api.post(`/sharing-tree/households/${h.id}/restore`)),
    permanentDelete: (h) => { setConfirmPerm(null); return run(setDeleting, h, "purging", () => api.delete(`/sharing-tree/households/${h.id}/permanent`)); },
  };

  // Sanitize filter values that the current privacy scope does not allow.
  const effective = useMemo(() => ({
    ...filters,
    reminder: emailHidden && filters.reminder === "no_email" ? "all" : filters.reminder,
    sort: namesHidden && filters.sort === "name" ? "newest" : filters.sort,
  }), [filters, emailHidden, namesHidden]);

  const filtered = useMemo(
    () => filterEnrolledHouseholds(households, effective, hiddenFields),
    [households, effective, hiddenFields],
  );

  const activeCount = households.filter((h) => !h.deleted_at).length;
  const removedCount = households.length - activeCount;
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, pageCount - 1); // clamp on refresh without resetting
  const pageRows = filtered.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const filteredActiveIds = filtered.filter((h) => !h.deleted_at).map((h) => h.id);
  const scopedSelected = filteredActiveIds.filter((id) => selectedIds.has(id));
  const allSelected = filteredActiveIds.length > 0 && scopedSelected.length === filteredActiveIds.length;
  const filterCount = activeHouseholdFilterCount(effective);
  const hasQuery = !!effective.query.trim();
  const narrowed = filterCount > 0 || hasQuery;

  function updateFilter(key, value) {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
    if (key !== "sort") onSelectAll?.([], false);
  }
  function clearFilters() {
    setFilters((f) => ({ ...DEFAULT_HOUSEHOLD_FILTERS, sort: f.sort }));
    setPage(0);
    onSelectAll?.([], false);
  }

  const first = filtered.length === 0 ? 0 : safePage * pageSize + 1;
  const last = safePage * pageSize + pageRows.length;

  const chips = [];
  const chipLabels = {
    status: { active: "Active only", removed: "Removed only" },
    reminder: { sent: "Reminder sent", not_sent: "Reminder not sent", no_email: "No email on file" },
    includes: { toys: "Has toy-age children", gift_cards: "Has gift-card ages", seniors: "Has seniors", duplicates: "Possible duplicates" },
  };
  ["status", "reminder", "includes"].forEach((k) => {
    if (effective[k] !== "all") chips.push({ key: k, label: chipLabels[k][effective[k]] || effective[k] });
  });
  if (effective.pickup !== "all") {
    chips.push({ key: "pickup", label: effective.pickup === "assigned" ? "Pickup assigned" : effective.pickup === "unassigned" ? "Pickup unassigned" : `Pickup: ${effective.pickup.replace(/^slot:/, "")}` });
  }

  return (
    <section className="fb-card p-4 sm:p-6" data-testid="enrolled-households-card" aria-labelledby="enrolled-households-heading">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 id="enrolled-households-heading" className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>
            Enrolled Households
            {removedCount > 0 && (
              <span className="ml-2 text-sm font-normal" style={{ color: "var(--fb-text-muted)" }}>
                ({activeCount} active, {removedCount} removed)
              </span>
            )}
          </h3>
        </div>
        {exportControl && (
          <div className="flex flex-col items-start sm:items-end gap-1">
            {exportControl}
            <p className="text-[11px] max-w-[260px] sm:text-right" style={{ color: "var(--fb-text-muted)" }}>
              CSV includes every active {year} household, not just the filtered view below.
            </p>
          </div>
        )}
      </div>

      {households.length === 0 ? (
        <div className="py-10 text-center rounded-xl border border-dashed text-sm" style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}>
          No households enrolled for {year}.{!isPriorYear && " Use \"New Signup\" to begin."}
        </div>
      ) : (
        <>
          {/* Toolbar */}
          <div className="rounded-xl p-3 mb-3 space-y-3" style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }}>
            <div className="flex gap-2 items-end">
              <div className="flex-1 min-w-0">
                <label htmlFor="enrolled-households-search" className="sr-only">Search enrolled households</label>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--fb-text-muted)" }} />
                  <input id="enrolled-households-search" data-testid="enrolled-households-search" type="search"
                    value={filters.query} onChange={(e) => updateFilter("query", e.target.value)}
                    placeholder={searchPlaceholder(hiddenFields)}
                    className="w-full text-sm rounded-lg border pl-9 pr-3 py-2 bg-white min-h-[40px]" style={{ borderColor: "var(--fb-border)" }} />
                </div>
              </div>
              <button type="button" onClick={() => setFiltersOpen((v) => !v)} aria-expanded={filtersOpen}
                aria-controls="enrolled-households-filter-panel"
                className="md:hidden inline-flex items-center gap-1.5 text-sm font-semibold px-3 rounded-lg border bg-white min-h-[40px]"
                style={{ borderColor: "var(--fb-border)" }}>
                <SlidersHorizontal className="w-4 h-4" /> Filters{filterCount > 0 ? ` (${filterCount})` : ""}
              </button>
            </div>
            <div id="enrolled-households-filter-panel"
              className={`${filtersOpen ? "grid" : "hidden"} md:grid grid-cols-2 md:grid-cols-5 gap-2`}>
              <FilterSelect id="enrolled-households-status" label="Status" value={effective.status} onChange={(v) => updateFilter("status", v)}>
                <option value="all">All (active + removed)</option>
                <option value="active">Active</option>
                <option value="removed">Removed</option>
              </FilterSelect>
              <FilterSelect id="enrolled-households-reminder" label="Reminder" value={effective.reminder} onChange={(v) => updateFilter("reminder", v)}>
                <option value="all">Any</option>
                <option value="sent">Sent</option>
                <option value="not_sent">Not sent</option>
                {!emailHidden && <option value="no_email">No email on file</option>}
              </FilterSelect>
              <FilterSelect id="enrolled-households-pickup" label="Pickup window" value={effective.pickup} onChange={(v) => updateFilter("pickup", v)}>
                <option value="all">Any</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
                {pickupSlots.map((s) => <option key={s} value={`slot:${s}`}>{s}</option>)}
              </FilterSelect>
              <FilterSelect id="enrolled-households-includes" label="Includes" value={effective.includes} onChange={(v) => updateFilter("includes", v)}>
                <option value="all">Anyone</option>
                <option value="toys">Toy ages (0–9)</option>
                <option value="gift_cards">Gift card ages (10–18)</option>
                <option value="seniors">Seniors (65+)</option>
                <option value="duplicates">Possible duplicates</option>
              </FilterSelect>
              <FilterSelect id="enrolled-households-sort" label="Sort" value={effective.sort} onChange={(v) => updateFilter("sort", v)}>
                <option value="newest">Newest signup</option>
                <option value="oldest">Oldest signup</option>
                {!namesHidden && <option value="name">Name A–Z</option>}
                <option value="id">Household ID</option>
              </FilterSelect>
            </div>
            {(chips.length > 0 || hasQuery) && (
              <div className="flex items-center gap-2 flex-wrap text-xs">
                {hasQuery && (
                  <button type="button" onClick={() => updateFilter("query", "")} aria-label="Remove search"
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium bg-white border" style={{ borderColor: "var(--fb-border)" }}>
                    "{effective.query.trim()}" <X className="w-3 h-3" />
                  </button>
                )}
                {chips.map((c) => (
                  <button key={c.key} type="button" onClick={() => updateFilter(c.key, "all")} aria-label={`Remove filter: ${c.label}`}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full font-medium"
                    style={{ background: "rgba(5,47,102,0.08)", color: "var(--fb-primary)", border: "1px solid rgba(5,47,102,0.18)" }}>
                    {c.label} <X className="w-3 h-3" />
                  </button>
                ))}
                <button type="button" id="enrolled-households-clear-filters" data-testid="enrolled-households-clear-filters"
                  onClick={clearFilters} className="font-semibold underline ml-1" style={{ color: "var(--fb-primary)" }}>
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Result count + selection */}
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <p id="enrolled-households-result-count" data-testid="enrolled-households-result-count" aria-live="polite"
              className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              {narrowed
                ? <><strong style={{ color: "var(--fb-text)" }}>{filtered.length}</strong> of {households.length} households match</>
                : <><strong style={{ color: "var(--fb-text)" }}>{households.length}</strong> households</>}
            </p>
            {writable && filteredActiveIds.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <label className="inline-flex items-center gap-2 text-sm font-medium cursor-pointer min-h-[36px]">
                  <input type="checkbox" checked={allSelected}
                    onChange={(e) => onSelectAll?.(filteredActiveIds, e.target.checked)}
                    className="w-4 h-4 rounded cursor-pointer accent-[#052F66]" data-testid="select-all-households-checkbox" />
                  {narrowed ? `Select all ${filteredActiveIds.length} active matches` : "Select all"}
                </label>
                {scopedSelected.length > 0 && (
                  <>
                    <button type="button" onClick={() => onSendSelected?.(scopedSelected)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border hover:bg-blue-50"
                      style={{ borderColor: "var(--fb-primary)", color: "var(--fb-primary)" }}
                      data-testid="send-selected-reminders-btn">
                      <Mail className="w-4 h-4" /> Send to {scopedSelected.length} selected
                    </button>
                    <button type="button" onClick={() => onSelectAll?.([], false)}
                      className="text-sm font-medium px-2 py-2 rounded-lg hover:bg-stone-100" style={{ color: "var(--fb-text-muted)" }}
                      data-testid="clear-household-selection-btn">
                      Clear selection
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {filtered.length === 0 ? (
            <div className="py-10 text-center rounded-xl border border-dashed text-sm space-y-3" style={{ borderColor: "var(--fb-border)", color: "var(--fb-text-muted)" }}
              data-testid="enrolled-households-no-matches">
              <p>No households match these filters.</p>
              <button type="button" onClick={clearFilters} className="text-sm font-semibold px-3 py-2 rounded-lg border bg-white"
                style={{ borderColor: "var(--fb-border)", color: "var(--fb-primary)" }}>
                Clear search and filters
              </button>
            </div>
          ) : (
            <ReportTableScroll label="Enrolled Households list">
              <div className="hidden md:grid grid-cols-[minmax(0,1.3fr)_minmax(0,1.35fr)_minmax(0,1.2fr)_auto] gap-x-4 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider sticky top-0 z-[1]"
                style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)" }} aria-hidden="true">
                <span>Household</span><span>Contact</span><span>Pickup &amp; reminder</span><span className="text-right">Members</span>
              </div>
              {pageRows.map((h) => (
                <EnrolledHouseholdRow key={h.id} h={h} writable={writable} canEditProfile={canEditProfile}
                  hiddenFields={hiddenFields}
                  selectable={writable && !h.deleted_at} selected={selectedIds.has(h.id)}
                  onToggleSelected={onToggleSelected} onEdit={onEdit}
                  expanded={expandedIds.has(h.id)} onToggleMembers={toggleMembers}
                  pickupSlots={pickupSlots} showFamilyItem={showFamilyItem}
                  busy={deleting[h.id]} confirmPerm={confirmPerm === h.id}
                  editingWindow={editingWindow === h.id} savingWindow={!!savingWindow[h.id]}
                  resetting={!!resettingReminder[h.id]} resending={!!resendingReminder[h.id]}
                  actions={actions} />
              ))}
            </ReportTableScroll>
          )}

          {filtered.length > 0 && (
            <nav className="flex items-center justify-between gap-3 flex-wrap mt-3" aria-label="Enrolled households pages">
              <div className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
                Showing <span className="tabular-nums font-semibold">{first}–{last}</span> of <span className="tabular-nums">{filtered.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <label htmlFor="enrolled-households-page-size" className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Per page</label>
                <select id="enrolled-households-page-size" value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                  className="text-sm rounded-lg border px-2 py-1.5 bg-white" style={{ borderColor: "var(--fb-border)" }}>
                  {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                <button type="button" id="enrolled-households-prev-page" data-testid="enrolled-households-prev-page"
                  onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0} aria-label="Previous page"
                  className="p-2 rounded-lg border bg-white disabled:opacity-40" style={{ borderColor: "var(--fb-border)" }}>
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm tabular-nums" aria-current="page">Page {safePage + 1} of {pageCount}</span>
                <button type="button" id="enrolled-households-next-page" data-testid="enrolled-households-next-page"
                  onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))} disabled={safePage >= pageCount - 1} aria-label="Next page"
                  className="p-2 rounded-lg border bg-white disabled:opacity-40" style={{ borderColor: "var(--fb-border)" }}>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </nav>
          )}
        </>
      )}
    </section>
  );
}
